import { MORE_IDS, TAB_BAR_IDS, destination, type DestinationId } from "@/lib/destinations";

import { formatBadgeCount } from "./badgeCount";

interface TabBarProps {
  active: DestinationId;
  onNavigate: (id: DestinationId) => void;
  dueCount?: number;
  /** Jobs whose last run failed. On その他, which is where 設定 now lives. */
  failedJobs?: number;
  /** Open the その他 sheet. */
  onOpenMore: () => void;
  /** Whether that sheet is up — その他 is a toggle, so it draws pressed. */
  moreOpen?: boolean;
}

/**
 * The phone tab bar: **four destinations and その他.**
 *
 * **The phone has no rail at all**, and that is a decision rather than a fallback: a 44px column
 * costs 11% of a 390px screen for what a thumb does better at the bottom of it. So this is the
 * *only* navigation on a phone, where on the desktop the rail is.
 *
 * Read off `docs/designs/Nihongonator - Mobile.dc.html`, which draws it identically on all nine
 * frames: 62px, `--panel`, five items, and the active one a **filled flag** — the rail's nobori
 * turned ninety degrees, with the same tighter tracking and the same white on the accent.
 *
 * It was a glyph in the accent colour with an English caption beneath it. Two characters at 14px
 * fit across a 390px bar five times over, so the caption bought nothing but a second line.
 *
 * **その他 lights while the active destination is one it holds** (日本-497). It is not a tab value
 * and never was one here: `active` is always a real `DestinationId`, and the sheet it opens is an
 * overlay. See `destinations.ts` for why that distinction is the whole of the difference between
 * this and the sheet deleted on 8 Aug 2026.
 *
 * Every target clears 44px. The design's 6px bottom padding is a literal value; real iOS also
 * wants `env(safe-area-inset-bottom)`, so both are applied and the larger wins.
 */
export function TabBar({
  active,
  onNavigate,
  dueCount,
  failedJobs,
  onOpenMore,
  moreOpen = false,
}: TabBarProps) {
  const items = TAB_BAR_IDS.map(destination);
  const moreActive = MORE_IDS.includes(active) || moreOpen;

  return (
    <nav
      aria-label="移動先"
      data-check="nav"
      // Which of the two navigations this is. `data-check="nav"` is on both, deliberately —
      // a check that wants "the navigation" should not have to know which shape it is in — so
      // `check-mobile.mjs` needs this to assert that the rail is ABSENT rather than merely narrow.
      data-nav="tabbar"
      className="flex h-[var(--tabbar-h)] shrink-0 items-center justify-around border-t border-line bg-panel box-border"
      style={{ paddingBottom: "max(6px, env(safe-area-inset-bottom))" }}
    >
      {items.map((item) => {
        const isActive = item.id === active;
        const count = item.id === "review" ? dueCount : undefined;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.id)}
            data-check={`nav.${item.id}`}
            aria-current={isActive ? "page" : undefined}
            aria-label={item.label}
            className="relative flex min-h-[var(--touch-min)] min-w-[56px] items-center justify-center"
          >
            <span
              className={[
                "font-jp text-[16px]/[1]",
                isActive
                  ? "rounded-[var(--r-2)] bg-accent px-[10px] py-[7px] font-bold tracking-[.1em] text-white"
                  : "font-medium tracking-[.04em] text-ink-3",
              ].join(" ")}
            >
              {item.word}
            </span>
            {count !== undefined && count > 0 && (
              /* Same formatter as the rail (日本-403), though this badge was never the one
                 clipping — it is absolutely positioned with `min-w` and padding, so four digits
                 grew rather than spilled. It shares the rule so the two navigations cannot draw
                 the same count two ways, and it gets narrower rather than wider for it: `1千+` at
                 14px is 30.8px against `1847`'s 33.6px. `font-mono-jp` for 千, as on the rail. */
              <span
                /* `badge.*` rather than `nav.badge.*` — see the note in `Rail.tsx`: the checks
                   count `[data-check^="nav."]` within the navigation and expect exactly five. */
                data-check={`badge.${item.id}`}
                title={count.toLocaleString()}
                className="absolute right-0.5 top-[-4px] min-w-[17px] rounded-[var(--r-2)] bg-accent px-1 text-center font-mono-jp text-[14px]/[23.8px] font-semibold text-white"
              >
                {formatBadgeCount(count)}
              </span>
            )}
          </button>
        );
      })}

      {/* その他 — a toggle, not a destination. Same face as an active tab when the sheet is up or
          when what you are looking at lives inside it, because both mean "you are in here". */}
      <button
        type="button"
        onClick={onOpenMore}
        data-check="nav.more"
        aria-expanded={moreOpen}
        aria-haspopup="dialog"
        aria-label="More"
        className="relative flex min-h-[var(--touch-min)] min-w-[56px] items-center justify-center"
      >
        <span
          className={[
            "font-jp text-[16px]/[1]",
            moreActive
              ? "rounded-[var(--r-2)] bg-accent px-[10px] py-[7px] font-bold tracking-[.1em] text-white"
              : "font-medium tracking-[.04em] text-ink-3",
          ].join(" ")}
        >
          その他
        </span>
        {failedJobs !== undefined && failedJobs > 0 && (
          /* The badge follows 設定 into the sheet, so the bar has to carry it — otherwise a failed
             job is only visible behind a tap, which is the regression the sheet's deletion named. */
          <span
            data-check="badge.more"
            title={failedJobs.toLocaleString()}
            className="absolute right-0 top-[2px] min-w-[17px] rounded-[var(--r-2)] bg-accent px-1 text-center font-mono-jp text-[14px]/[23.8px] font-semibold text-white"
          >
            {formatBadgeCount(failedJobs)}
          </span>
        )}
      </button>
    </nav>
  );
}
